/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/hooks/useNotifications.ts
 * PURPOSE: Custom React hooks for state management
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-09-01
 * COMPLIANCE: SDLC 6.5.0 + Zero-Mock Tolerance
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (React Best Practices)
 * SPRINT GOVERNANCE: Rule 6/7: Performance SLAs (reconnect-storm containment)
 *
 * @business-context
 * - Domain: React Hooks, State Management, Vietnamese F&B Business Logic
 * - Impact: Code Reusability, State Consistency, Developer Productivity
 * - Stakeholders: Development Team, Frontend Engineers
 *
 * @technical-architecture
 * - Layer: Business Logic Layer, Custom Hooks
 * - Pattern: React Hooks Pattern, State Management, API Integration
 * - Dependencies: React 18, TypeScript, API Services
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/FRONTEND-BACKEND-GAP-ANALYSIS-SPRINT10-13.md
 * - Issue: #710 (BOT-021, P1) — WebSocket reconnect had a fixed 3s retry with no
 *   backoff/cap, risking a client-side DoS-against-self on server outage. Fixed with
 *   exponential backoff (capped) + jitter, matching the pattern already used in
 *   `frontend/src/services/approvalNotificationService.ts` and
 *   `frontend/src/hooks/useORToolsScheduler.ts`.
 *  */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { WS_BASE_URL } from '@/services/api/config';
import { auth_state_use__manages_session__returns_actions } from '@/features/auth';
import { apiClient } from '@/services/api/client';

// Notification types
export interface Notification {
  id: string;
  type: 'info' | 'warning' | 'success' | 'task' | 'schedule' | 'system';
  title: string;
  message: string;
  timestamp: string;
  isRead: boolean;
  actionUrl?: string;
  priority: 'low' | 'medium' | 'high';
  metadata?: {
    date?: string;
    status?: string;
    userId?: string;
    relatedId?: string;
  };
}

// WebSocket message types
// ISS-018-F: Added attendance.update / task.assigned / schedule.changed per SPEC-0407 §2.2
interface NotificationMessage {
  type: 'notification' | 'notification_read' | 'notification_deleted'
    | 'attendance.update' | 'task.assigned' | 'schedule.changed' | 'alert.triggered';
  data: Notification | { id: string } | Record<string, unknown>;
}

// GN-1: Wire format returned by GET /api/v1/notifications (backend/app/api/v1/__init__.py).
// The REST payload is snake_case and uses the backend NotificationType /
// NotificationPriority enums, so it must be mapped onto the UI-facing shape above.
interface NotificationApiItem {
  id: string;
  title: string;
  title_vietnamese?: string | null;
  message: string;
  message_vietnamese?: string | null;
  type: string;
  is_read: boolean;
  created_at: string;
  priority?: string | null;
  action_url?: string | null;
}

// backend NotificationType (models/notification.py) → UI category
const API_TYPE_TO_UI: Record<string, Notification['type']> = {
  schedule_change: 'schedule',
  schedule_reminder: 'schedule',
  attendance_reminder: 'task',
  approval_required: 'task',
  leave_request: 'task',
  overtime_alert: 'warning',
  announcement: 'info',
  payroll: 'info',
  system: 'system',
};

// backend NotificationPriority → UI priority (urgent collapses onto high)
const API_PRIORITY_TO_UI: Record<string, Notification['priority']> = {
  low: 'low',
  medium: 'medium',
  high: 'high',
  urgent: 'high',
};

export function mapApiNotification(item: NotificationApiItem): Notification {
  return {
    id: String(item.id),
    type: API_TYPE_TO_UI[item.type] ?? 'info',
    // Vietnamese-first per platform localisation rule, fall back to base field
    title: item.title_vietnamese || item.title,
    message: item.message_vietnamese || item.message,
    timestamp: item.created_at,
    isRead: Boolean(item.is_read),
    actionUrl: item.action_url ?? undefined,
    priority: API_PRIORITY_TO_UI[item.priority ?? ''] ?? 'medium',
  };
}

// BOT-021 (#710): WebSocket reconnect backoff config — a fixed 3s retry with no
// cap meant every client hammered the server every 3s forever on an outage
// (client-side DoS-against-self). Exponential backoff with a ceiling + jitter,
// consistent with approvalNotificationService.ts / useORToolsScheduler.ts.
const WS_RECONNECT_BASE_DELAY_MS = 3000;
const WS_RECONNECT_MAX_DELAY_MS = 60000; // 1 minute ceiling
const WS_RECONNECT_JITTER_MIN = 0.1; // ±10%
const WS_RECONNECT_JITTER_MAX = 0.2; // ±20%

/**
 * Exponential backoff (capped) + jitter for WebSocket reconnect delay.
 * No hard cap on attempt count by design — the notification channel should
 * keep trying to reconnect indefinitely, just increasingly spaced out.
 */
function getReconnectDelayMs(attempt: number): number {
  const exponentialDelay = WS_RECONNECT_BASE_DELAY_MS * Math.pow(2, attempt);
  const cappedDelay = Math.min(exponentialDelay, WS_RECONNECT_MAX_DELAY_MS);

  // Jitter avoids a thundering herd of clients retrying in lockstep.
  const jitterMagnitude = WS_RECONNECT_JITTER_MIN + Math.random() * (WS_RECONNECT_JITTER_MAX - WS_RECONNECT_JITTER_MIN);
  const jitterSign = Math.random() < 0.5 ? -1 : 1;

  return Math.round(cappedDelay * (1 + jitterSign * jitterMagnitude));
}

// S95L-016: Module-level shared state — all useNotifications() instances share same data
// Required because ManagerDashboard (bell badge) and NotificationDropdown (list)
// both call useNotifications() — must see same notifications array
let _sharedNotifications: Notification[] = [];
let _sharedListeners: Set<() => void> = new Set();

function _setSharedNotifications(updater: (prev: Notification[]) => Notification[]) {
  _sharedNotifications = updater(_sharedNotifications);
  _sharedListeners.forEach(fn => fn());
}

function _useSharedNotifications(): [Notification[], (updater: (prev: Notification[]) => Notification[]) => void] {
  const [, forceUpdate] = useState(0);
  useEffect(() => {
    const listener = () => forceUpdate(n => n + 1);
    _sharedListeners.add(listener);
    return () => { _sharedListeners.delete(listener); };
  }, []);
  return [_sharedNotifications, _setSharedNotifications];
}

export const useNotifications = () => {
  const [notifications, setNotifications] = _useSharedNotifications();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const wsRef = useRef<WebSocket | null>(null);
  // BOT-021 (#710): reconnect bookkeeping — attempt count drives the exponential
  // backoff delay, resets to 0 only on a successful `onopen`.
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards against a reconnect firing after intentional disconnect (unmount, or
  // user change) — the pending setTimeout has no other way to know the owning
  // connection was torn down.
  const shouldReconnectRef = useRef(true);
  const { user, accessToken } = auth_state_use__manages_session__returns_actions();
  // ISS-018-F: QueryClient for invalidating manager dashboard on WS events
  const queryClient = useQueryClient();

  // Initialize notifications from API
  // GN-1: REST is the source of truth on load — this is what makes read-state
  // survive a page reload. WebSocket then layers live pushes on top.
  const initializeNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get<NotificationApiItem[]>('/notifications');
      const items = Array.isArray(response.data) ? response.data : [];
      setNotifications(() => items.map(mapApiNotification));
    } catch (error) {
      // Leave whatever WebSocket/FCM already pushed in place rather than
      // blanking the list on a transient fetch failure.
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // WebSocket connection management
  const connectWebSocket = useCallback(() => {
    if (!user?.id || !accessToken || wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    // A deliberate (re)connect re-arms auto-reconnect for this connection's
    // lifetime; disconnectWebSocket() is what turns it back off.
    shouldReconnectRef.current = true;

    try {
      // BOT-021 (#710): the backend WS endpoint closes with code 4001 if the
      // `token` query param is missing — it's not optional like the frontend
      // proxy layer (nginx) makes it look once routing itself is fixed.
      const ws = new WebSocket(`${WS_BASE_URL}/ws/${user.id}?token=${accessToken}`);

      ws.onopen = () => {

        setIsConnected(true);
        // BOT-021 (#710): successful connection resets the backoff counter.
        reconnectAttemptsRef.current = 0;
      };

      ws.onmessage = (event) => {
        try {
          const message: NotificationMessage = JSON.parse(event.data);
          
          switch (message.type) {
            case 'notification':
              const newNotification = message.data as Notification;
              setNotifications(prev => [newNotification, ...prev]);

              // ISS-018-F: Invalidate manager dashboard queries on attendance-related events
              // This enables <500ms real-time updates per BR-DASH-001 §2.4 / SPEC-0407 §2.2
              if (newNotification.type === 'warning' || newNotification.type === 'success' || newNotification.type === 'task') {
                queryClient.invalidateQueries({ queryKey: ['manager'] });
              }

              // Show browser notification if permission granted
              if (Notification.permission === 'granted') {
                new Notification(newNotification.title, {
                  body: newNotification.message,
                  icon: '/logo-192.png',
                  tag: newNotification.id
                });
              }
              break;

            case 'notification_read':
              const readData = message.data as { id: string };
              setNotifications(prev => 
                prev.map(n => n.id === readData.id ? { ...n, isRead: true } : n)
              );
              break;

            case 'notification_deleted':
              const deleteData = message.data as { id: string };
              setNotifications(prev => prev.filter(n => n.id !== deleteData.id));
              break;

            // ISS-018-F: SPEC-0407 §2.2 WebSocket events → invalidate dashboard <500ms
            case 'attendance.update':
            case 'task.assigned':
            case 'schedule.changed':
            case 'alert.triggered':
              queryClient.invalidateQueries({ queryKey: ['manager'] });
              break;
          }
        } catch (error) {

        }
      };

      ws.onclose = () => {

        setIsConnected(false);

        // BOT-021 (#710): don't reconnect after an intentional disconnect
        // (component unmounted, or user changed) — the fixed-delay retry used
        // to fire regardless, hammering the server every 3s forever on outage.
        if (!shouldReconnectRef.current || !user?.id) {
          return;
        }

        const attempt = reconnectAttemptsRef.current;
        reconnectAttemptsRef.current += 1;
        const delay = getReconnectDelayMs(attempt);

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          if (shouldReconnectRef.current && user?.id) {
            connectWebSocket();
          }
        }, delay);
      };

      ws.onerror = (error) => {

        setIsConnected(false);
      };

      wsRef.current = ws;
    } catch (error) {

    }
  }, [user?.id, accessToken]);

  // Disconnect WebSocket
  const disconnectWebSocket = useCallback(() => {
    // BOT-021 (#710): stop any scheduled reconnect — a pending setTimeout would
    // otherwise call connectWebSocket() after the component has unmounted (or
    // the user changed), reopening a socket nobody is listening to anymore.
    shouldReconnectRef.current = false;
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
      setIsConnected(false);
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      // Optimistic update
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      );

      // GN-1: persist read-state server-side so it survives a reload
      await apiClient.patch(`/notifications/${notificationId}/read`);

      // Send WebSocket message to sync across sessions
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'mark_read',
          data: { id: notificationId }
        }));
      }
    } catch (error) {

      // Revert optimistic update
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, isRead: false } : n)
      );
    }
  }, []);

  // Remove notification
  const removeNotification = useCallback(async (notificationId: string) => {
    try {
      // Optimistic update
      setNotifications(prev => prev.filter(n => n.id !== notificationId));

      // GN-1: persist the deletion server-side
      await apiClient.delete(`/notifications/${notificationId}`);

      // Send WebSocket message to sync across sessions
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'delete',
          data: { id: notificationId }
        }));
      }
    } catch (error) {
      // GN-1: the DELETE is now a real server call, so a failure means the row
      // still exists. Resync from REST instead of leaving the UI lying — the
      // item would otherwise silently reappear on the next reload.
      initializeNotifications();
    }
  }, [initializeNotifications]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    try {
      const unreadIds = notifications.filter(n => !n.isRead).map(n => n.id);
      
      // Optimistic update
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));

      // GN-1: persist bulk read-state server-side
      await apiClient.patch('/notifications/mark-all-read');

      // Send WebSocket message to sync across sessions
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'mark_all_read',
          data: { ids: unreadIds }
        }));
      }
    } catch (error) {

      // Revert optimistic update
      initializeNotifications();
    }
  }, [notifications, initializeNotifications]);

  // Request browser notification permission
  const requestNotificationPermission = useCallback(async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }
    return Notification.permission === 'granted';
  }, []);

  // S95L-016: FCM push → notification state (production wiring)
  // SW background push: sw.js postMessage({ type: 'FCM_PUSH_RECEIVED' })
  // Foreground push: firebase.ts dispatchEvent(CustomEvent('fcm-notification'))
  useEffect(() => {
    const handleSWMessage = (event: MessageEvent) => {
      if (event.data?.type === 'FCM_PUSH_RECEIVED') {
        const { title, body, data } = event.data.payload ?? {};
        if (!title) return;
        const pushNotif: Notification = {
          id: `fcm-${Date.now()}`,
          type: 'info',
          title,
          message: body ?? '',
          timestamp: new Date().toISOString(),
          isRead: false,
          actionUrl: data?.url,
          priority: 'high',
        };
        setNotifications(prev => [pushNotif, ...prev]);
        queryClient.invalidateQueries({ queryKey: ['manager'] });
      }
    };

    const handleForegroundPush = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail?.title) return;
      const pushNotif: Notification = {
        id: `fcm-fg-${Date.now()}`,
        type: 'info',
        title: detail.title,
        message: detail.body ?? '',
        timestamp: detail.timestamp ?? new Date().toISOString(),
        isRead: false,
        actionUrl: detail.data?.url,
        priority: 'high',
      };
      setNotifications(prev => [pushNotif, ...prev]);
      queryClient.invalidateQueries({ queryKey: ['manager'] });
    };

    navigator.serviceWorker?.addEventListener('message', handleSWMessage);
    window.addEventListener('fcm-notification', handleForegroundPush);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
      window.removeEventListener('fcm-notification', handleForegroundPush);
    };
  }, [queryClient]);

  // Initialize on mount and user change
  useEffect(() => {
    if (user?.id) {
      initializeNotifications();
      connectWebSocket();

      // Request notification permission
      requestNotificationPermission();
    }

    return () => {
      disconnectWebSocket();
    };
  }, [user?.id, initializeNotifications, connectWebSocket, disconnectWebSocket, requestNotificationPermission]);

  // Computed values
  const unreadCount = notifications.filter(n => !n.isRead).length;
  const hasHighPriorityUnread = notifications.some(n => !n.isRead && n.priority === 'high');

  return {
    notifications,
    unreadCount,
    hasHighPriorityUnread,
    isLoading,
    isConnected,
    markAsRead,
    removeNotification,
    markAllAsRead,
    requestNotificationPermission,
    refreshNotifications: initializeNotifications
  };
};